import { EVENT_SITES } from '@crossbeam/itly';

import useIteratively from '@/composables/useIteratively';

const EVENT_NAME = 'partner_wishlist_action';

type BaseProperties = {
  event_site: string;
  type: string;
};

function usePartnerTrackerTracking() {
  const { iteratively } = useIteratively();

  function track(properties: BaseProperties & Record<string, unknown>) {
    // @ts-expect-error itly JSDoc types event.properties as string, but it accepts an object at runtime
    iteratively.track({ name: EVENT_NAME, properties });
  }

  function trackUploadModalOpened() {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_MODAL,
      type: 'upload_modal_opened',
    });
  }

  function trackCsvUploaded(totalRows: number, hasErrors: boolean) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_MODAL,
      type: 'csv_uploaded',
      total_rows: totalRows,
      has_errors: hasErrors,
    });
  }

  function trackUploadConfirmed(total: number, onCrossbeamCount: number) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_MODAL,
      type: 'upload_confirmed',
      total_partners: total,
      on_crossbeam_count: onCrossbeamCount,
    });
  }

  function trackTemplateDownloaded() {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_MODAL,
      type: 'template_downloaded',
    });
  }

  function trackPostUploadInviteSent(
    option: string,
    count: number,
    onCrossbeamCount: number,
    hasMessage: boolean,
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_SUMMARY_MODAL,
      type: 'post_upload_invite_sent',
      invite_option: option,
      invite_count: count,
      on_crossbeam_count: onCrossbeamCount,
      has_message: hasMessage,
    });
  }

  function trackSingleInviteSent(isOnCrossbeam: boolean, contactCount: number) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_INVITE_MODAL,
      type: 'single_invite_sent',
      is_on_crossbeam: isOnCrossbeam,
      contact_count: contactCount,
    });
  }

  function trackBulkInviteSent(
    count: number,
    onCrossbeamCount: number,
    hasMessage: boolean,
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_BULK_INVITE_MODAL,
      type: 'bulk_invite_sent',
      invite_count: count,
      on_crossbeam_count: onCrossbeamCount,
      has_message: hasMessage,
    });
  }

  function trackPartnerRemoved() {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_TABLE,
      type: 'partner_removed',
    });
  }

  function trackBulkRemove(count: number) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_BULK_REMOVE_MODAL,
      type: 'bulk_remove',
      remove_count: count,
    });
  }

  function trackOwnerChanged() {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_TABLE,
      type: 'owner_changed',
    });
  }

  function trackContactsUpdated() {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_TABLE,
      type: 'contacts_updated',
    });
  }

  // Fired when a request recipient submits a decline WITH a reason
  // (Partner Tracker only). `reason` is the DeclineReasonType enum value.
  function trackDeclineSubmitted(reason: string, hasNote: boolean) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_DECLINE_MODAL,
      type: 'decline_submitted',
      decline_reason: reason,
      has_message: hasNote,
    });
  }

  // Fired when the sender opens the "View reason" modal on a declined row.
  // `reason` may be null when the decliner chose "prefer not to say" (the BE
  // masks it before it reaches the sender).
  function trackDeclineReasonViewed(reason: string | null, hasNote: boolean) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_VIEW_REASON_MODAL,
      type: 'decline_reason_viewed',
      ...(reason != null && { decline_reason: reason }),
      has_message: hasNote,
    });
  }

  // Fired when the sender clicks "Invite again" from the view-reason modal.
  function trackReinviteFromDecline() {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_VIEW_REASON_MODAL,
      type: 'reinvite_from_decline',
    });
  }

  return {
    trackUploadModalOpened,
    trackCsvUploaded,
    trackUploadConfirmed,
    trackTemplateDownloaded,
    trackPostUploadInviteSent,
    trackSingleInviteSent,
    trackBulkInviteSent,
    trackPartnerRemoved,
    trackBulkRemove,
    trackOwnerChanged,
    trackContactsUpdated,
    trackDeclineSubmitted,
    trackDeclineReasonViewed,
    trackReinviteFromDecline,
  };
}

export { usePartnerTrackerTracking };
